import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('nombre', formData.nombre);
      formBody.append('email', formData.email);
      formBody.append('mensaje', formData.mensaje);

      const response = await fetch('https://readdy.ai/api/form/d5gjt1348noljcu3pja0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ nombre: '', email: '', mensaje: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-24 bg-white relative overflow-hidden">
      {/* Patrón de fondo */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(#FF6B9D 1px, transparent 1px), linear-gradient(90deg, #FF6B9D 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {/* Lado izquierdo */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-purple-900 mb-4">
                Hablemos de tu proyecto
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Estoy aquí para ayudarte a hacer crecer tu marca en redes sociales. Cuéntame sobre tu proyecto y descubre cómo podemos trabajar juntas para alcanzar tus objetivos.
              </p>
            </div>

            {/* Botones de contacto rápido */}
            <div className="space-y-4">
              <a
                href="https://wa.me/message/RKPZLG3TDGMLD1"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 px-6 py-4 bg-green-500 text-white font-semibold rounded-2xl hover:bg-green-600 hover:shadow-lg transition-all cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-2xl"></i>
                </div>
                <div className="text-left">
                  <div className="text-sm opacity-90">Escríbeme por</div>
                  <div className="font-bold">WhatsApp</div>
                </div>
              </a>

              <a
                href="https://www.instagram.com/paty_publicidad?igsh=MWN3ZWJqazA0OGR6dw%3D%3D&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 px-6 py-4 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white font-semibold rounded-2xl hover:shadow-lg transition-all cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-instagram-line text-2xl"></i>
                </div>
                <div className="text-left">
                  <div className="text-sm opacity-90">Sígueme en</div>
                  <div className="font-bold">Instagram</div>
                </div>
              </a>

              <a
                href="mailto:patypublicidad16@gmail.com"
                className="flex items-center gap-4 px-6 py-4 border-2 border-purple-600 text-purple-600 font-semibold rounded-2xl hover:bg-purple-50 transition-all cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-mail-line text-2xl"></i>
                </div>
                <div className="text-left">
                  <div className="text-sm opacity-90">Envíame un</div>
                  <div className="font-bold">Email</div>
                </div>
              </a>
            </div>
          </div>

          {/* Lado derecho - Formulario */}
          <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-3xl p-8 md:p-10">
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-6">
              {/* Campo Nombre */}
              <div className="relative">
                <input
                  type="text"
                  name="nombre"
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  required
                  className="w-full px-4 py-4 bg-white border-b-2 border-pink-300 focus:border-purple-600 outline-none transition-all rounded-t-xl text-sm"
                  placeholder="Tu nombre"
                />
                <label
                  htmlFor="nombre"
                  className="absolute left-4 -top-2 text-xs text-purple-600 font-medium bg-white px-2 rounded"
                >
                  Nombre
                </label>
              </div>

              {/* Campo Email */}
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-4 bg-white border-b-2 border-pink-300 focus:border-purple-600 outline-none transition-all rounded-t-xl text-sm"
                  placeholder="tu@email.com"
                />
                <label
                  htmlFor="email"
                  className="absolute left-4 -top-2 text-xs text-purple-600 font-medium bg-white px-2 rounded"
                >
                  Email
                </label>
              </div>

              {/* Campo Mensaje */}
              <div className="relative">
                <textarea
                  name="mensaje"
                  id="mensaje"
                  value={formData.mensaje}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({ ...formData, mensaje: e.target.value });
                    }
                  }}
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-4 bg-white border-b-2 border-pink-300 focus:border-purple-600 outline-none transition-all rounded-t-xl resize-none text-sm"
                  placeholder="Cuéntame sobre tu proyecto..."
                ></textarea>
                <label
                  htmlFor="mensaje"
                  className="absolute left-4 -top-2 text-xs text-purple-600 font-medium bg-white px-2 rounded"
                >
                  Mensaje
                </label>
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.mensaje.length}/500
                </div>
              </div>

              {/* Botón de envío */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Enviando...' : 'Enviar mensaje'}
              </button>

              {/* Botón adicional de Email */}
              <a
                href="mailto:patypublicidad16@gmail.com"
                className="w-full px-8 py-4 border-2 border-purple-600 text-purple-600 font-bold rounded-2xl hover:bg-purple-50 transition-all cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-mail-line text-xl"></i>
                Contactar por Email
              </a>

              {/* Mensajes de estado */}
              {submitStatus === 'success' && (
                <div className="p-4 bg-green-100 text-green-700 rounded-xl text-center text-sm font-medium">
                  ¡Mensaje enviado! Te contactaré pronto 💕
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="p-4 bg-red-100 text-red-700 rounded-xl text-center text-sm font-medium">
                  Hubo un error. Por favor intenta de nuevo.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
